import {ElementRef, Injectable} from '@angular/core';
import {GeoJSONSource, GeoJSONSourceSpecification, LayerSpecification, LngLatBounds, Map as MapLibreMap, MapLayerMouseEvent, Popup, SymbolLayerSpecification} from 'maplibre-gl';
import {DatosMapaModel} from '@shared/models/datos-mapa.model';
import {Store} from '@ngxs/store';
import {MainState, defaultAccountSettings} from '@app/store/main.store';
import {Router} from '@angular/router';

const MAP_IDS = {
  source: 'map-markers',
  clusterHalo: 'map-clusters',
  clusterInner: 'map-cluster-inner',
  clusterCount: 'map-cluster-count',
  points: 'map-points',
  spiderfyLines: 'map-spiderfy-lines',
  spiderfyPoints: 'map-spiderfy-points'
} as const;
type PixelPoint = [number, number];
type ClusterLeaves = Awaited<ReturnType<GeoJSONSource['getClusterLeaves']>>;
const CLUSTER_LAYERS = [MAP_IDS.clusterInner, MAP_IDS.clusterCount];
const MARKER_IMAGE = {id: 'gis-marker', url: 'assets/images/marker-icon-2x.png', pixelRatio: 2} as const;
const MARKER_LAYOUT: SymbolLayerSpecification['layout'] = {
  'icon-image': MARKER_IMAGE.id,
  'icon-anchor': 'bottom',
  'icon-allow-overlap': true,
  'icon-ignore-placement': true
};

@Injectable()
export class MapService {
  map: MapLibreMap;
  private popup?: Popup;
  private mapReady?: Promise<void>;
  private resolveMapReady?: () => void;
  private clusterRequest?: {clusterId: number};
  private defaultTextFields = new Map<string, unknown>();
  private languageSubscription = this.store.select(MainState.accountSettings).subscribe((settings) => this.applyLanguage(settings?.locale || defaultAccountSettings.locale));

  constructor(private store: Store, private router: Router) {}

  createMap(el: ElementRef): void {
    if (this.map) return;

    this.map = new MapLibreMap({
      container: el.nativeElement,
      // El estilo remoto aporta las capas base y sus nombres dependen del proveedor.
      style: 'https://tiles.openfreemap.org/styles/bright',
      maxZoom: 19,
      pitch: 0,
      maxPitch: 0,
      cancelPendingTileRequestsWhileZooming: true,
      attributionControl: false
    });
    const map = this.map;
    this.mapReady = new Promise((resolve, reject) => {
      this.resolveMapReady = resolve;
      map.once('error', (event) => reject(event.error));
      map.once('load', async () => {
        if (map !== this.map) {
          resolve();
          return;
        }
        try {
          this.prepareStyle();
          this.applyLanguage(this.store.selectSnapshot(MainState.accountSettings)?.locale || defaultAccountSettings.locale);
          await this.installMarkerLayers(map);
          if (map !== this.map) {
            resolve();
            return;
          }
          resolve();
        } catch (error) {
          reject(error);
        } finally {
          this.resolveMapReady = undefined;
        }
      });
    });
  }

  async render(data: DatosMapaModel[]): Promise<boolean> {
    await this.mapReady;
    const map = this.map;
    const source = map?.getSource(MAP_IDS.source) as GeoJSONSource;
    if (!source) return false;
    this.clearSpiderfy();
    this.closePopup();
    const features = data.map((location) => {
      const {x, y} = location.coordenadas;

      return {
        type: 'Feature' as const,
        geometry: {type: 'Point' as const, coordinates: [y, x]},
        properties: {
          nombre: location.nombre,
          actions: JSON.stringify(location.actions || []),
          minLng: y,
          maxLng: y,
          minLat: x,
          maxLat: x
        }
      };
    });
    await source.setData({type: 'FeatureCollection', features});
    return map === this.map;
  }

  private async installMarkerLayers(map: MapLibreMap): Promise<void> {
    if (map !== this.map) return;

    if (!map.hasImage(MARKER_IMAGE.id)) {
      const image = await map.loadImage(MARKER_IMAGE.url);

      if (map !== this.map) return;
      map.addImage(MARKER_IMAGE.id, image.data, {pixelRatio: MARKER_IMAGE.pixelRatio});
    }

    if (map !== this.map) return;

    const source: GeoJSONSourceSpecification = {
      type: 'geojson',
      data: {type: 'FeatureCollection', features: []},
      maxzoom: 20,
      cluster: true,
      clusterMaxZoom: 19,
      clusterRadius: 70,
      clusterProperties: {
        minLng: ['min', ['get', 'minLng']],
        maxLng: ['max', ['get', 'maxLng']],
        minLat: ['min', ['get', 'minLat']],
        maxLat: ['max', ['get', 'maxLat']]
      }
    };

    map.addSource(MAP_IDS.source, source);
    const layers: LayerSpecification[] = [
      {
        id: MAP_IDS.clusterHalo,
        type: 'circle',
        source: MAP_IDS.source,
        filter: ['has', 'point_count'],
        paint: {'circle-color': '#90caf9', 'circle-radius': 20, 'circle-opacity': 0.45, 'circle-blur': 0.2}
      },
      {
        id: MAP_IDS.clusterInner,
        type: 'circle',
        source: MAP_IDS.source,
        filter: ['has', 'point_count'],
        paint: {'circle-color': '#1976d2', 'circle-radius': 14, 'circle-opacity': 0.95}
      },
      {
        id: MAP_IDS.clusterCount,
        type: 'symbol',
        source: MAP_IDS.source,
        filter: ['has', 'point_count'],
        layout: {'text-field': ['to-string', ['get', 'point_count']], 'text-font': ['Noto Sans Bold'], 'text-size': 11},
        paint: {'text-color': '#ffffff'}
      },
      {
        id: MAP_IDS.points,
        type: 'symbol',
        source: MAP_IDS.source,
        filter: ['!', ['has', 'point_count']],
        layout: MARKER_LAYOUT
      }
    ];
    layers.forEach((layer) => map.addLayer(layer));
    map.on('click', [MAP_IDS.clusterInner, MAP_IDS.clusterCount], (event) => this.zoomCluster(event));
    map.on('mouseenter', [...CLUSTER_LAYERS, MAP_IDS.points], () => map.getCanvas().style.cursor = 'pointer');
    map.on('mouseleave', [...CLUSTER_LAYERS, MAP_IDS.points], () => map.getCanvas().style.cursor = '');
    map.on('click', [MAP_IDS.points, MAP_IDS.spiderfyPoints], (event) => this.openFeaturePopup(event));
    map.on('click', (event) => {
      const layers = map.getLayer(MAP_IDS.spiderfyPoints) ? [...CLUSTER_LAYERS, MAP_IDS.spiderfyPoints] : CLUSTER_LAYERS;
      if (!map.queryRenderedFeatures(event.point, {layers}).length) {
        this.clearSpiderfy();
      }
    });
    map.on('zoomstart', () => this.clearSpiderfy());
  }

  private async zoomCluster(event: MapLayerMouseEvent): Promise<void> {
    const map = this.map;
    if (!map) return;
    const cluster = event.features?.[0] || map.queryRenderedFeatures(event.point, {layers: [MAP_IDS.clusterInner]})[0];
    const clusterId = cluster?.properties?.['cluster_id'];
    if (clusterId === undefined) return;
    const source = map.getSource(MAP_IDS.source) as GeoJSONSource;
    if (!source) return;
    const pointCount = Number(cluster.properties?.['point_count']);
    if (this.clusterRequest?.clusterId === clusterId) return;
    const minLng = Number(cluster.properties?.['minLng']);
    const maxLng = Number(cluster.properties?.['maxLng']);
    const minLat = Number(cluster.properties?.['minLat']);
    const maxLat = Number(cluster.properties?.['maxLat']);
    if (minLng !== maxLng || minLat !== maxLat) {
      this.clearSpiderfy();
      map.fitBounds([[minLng, minLat], [maxLng, maxLat]], {padding: 50, maxZoom: map.getMaxZoom(), duration: 400});
      return;
    }

    this.clearSpiderfy();
    const promise = source.getClusterLeaves(clusterId, pointCount, 0);
    const request = {clusterId};
    this.clusterRequest = request;
    promise.then((leaves) => {
      if (this.clusterRequest !== request || this.map !== map || map.getSource(MAP_IDS.source) !== source) return;
      if (cluster.geometry?.type !== 'Point') return;
      this.spiderfyCluster(map, leaves, cluster.geometry.coordinates as [number, number]);
    }).catch(() => {
      if (this.clusterRequest === request) this.clusterRequest = undefined;
    }).finally(() => {
      if (this.clusterRequest === request) this.clusterRequest = undefined;
    });
  }

  private spiderfyCluster(map: MapLibreMap, leaves: ClusterLeaves, center: [number, number]): void {
    if (leaves.length < 2) return;

    // Usamos píxeles para que los marcadores permanezcan separados en cualquier zoom.
    const centerPoint = map.project(center);
    const centerPixel: PixelPoint = [centerPoint.x, centerPoint.y];
    const positions = leaves.length < 9
      ? this.generateCirclePositions(leaves.length, centerPixel)
      : this.generateSpiralPositions(leaves.length, centerPixel);
    const lineFeatures = leaves.map((_, index) => ({
      type: 'Feature' as const,
      geometry: {type: 'LineString' as const, coordinates: [center, map.unproject(positions[index]).toArray()]},
      properties: {}
    }));
    const pointFeatures = leaves.map((leaf, index) => ({
      ...leaf,
      geometry: {type: 'Point' as const, coordinates: map.unproject(positions[index]).toArray()},
    }));

    map.addSource(MAP_IDS.spiderfyLines, {type: 'geojson', data: {type: 'FeatureCollection', features: lineFeatures}});
    map.addSource(MAP_IDS.spiderfyPoints, {type: 'geojson', data: {type: 'FeatureCollection', features: pointFeatures}});
    map.addLayer({id: MAP_IDS.spiderfyLines, type: 'line', source: MAP_IDS.spiderfyLines, paint: {'line-color': '#707070', 'line-width': 1.5}});
    map.addLayer({id: MAP_IDS.spiderfyPoints, type: 'symbol', source: MAP_IDS.spiderfyPoints, layout: MARKER_LAYOUT});
  }

  private generateCirclePositions(count: number, center: PixelPoint): PixelPoint[] {
    const radius = Math.max(35, 25 * (2 + count) / (Math.PI * 2));

    return Array.from({length: count}, (_, index) => [
      center[0] + radius * Math.cos(index * Math.PI * 2 / count),
      center[1] + radius * Math.sin(index * Math.PI * 2 / count)
    ]);
  }

  private generateSpiralPositions(count: number, center: PixelPoint): PixelPoint[] {
    let legLength = 11;
    let angle = 0;
    const positions = new Array<PixelPoint>(count);

    for (let index = count; index >= 0; index--) {
      if (index < count) {
        positions[index] = [
          center[0] + legLength * Math.cos(angle),
          center[1] + legLength * Math.sin(angle)
        ];
      }

      angle += 28 / legLength + index * 0.0005;
      legLength += 5 * Math.PI * 2 / angle;
    }
    return positions;
  }

  private clearSpiderfy(): void {
    this.clusterRequest = undefined;
    [MAP_IDS.spiderfyPoints, MAP_IDS.spiderfyLines].forEach((id) => {
      if (this.map?.getLayer(id)) this.map.removeLayer(id);
      if (this.map?.getSource(id)) this.map.removeSource(id);
    });
  }

  fitBounds(data: DatosMapaModel[]): void {
    const coordinates = data?.filter((item) => item.coordenadas).map((item) => [item.coordenadas.y, item.coordenadas.x] as [number, number]);
    if (!this.map || !coordinates?.length) return;
    if (coordinates.length === 1) {
      this.map.jumpTo({center: coordinates[0], zoom: Math.min(15, this.map.getMaxZoom())});
      return;
    }
    this.map.fitBounds(coordinates.reduce((bounds, point) => bounds.extend(point), new LngLatBounds()), {padding: 50, maxZoom: 6, duration: 0});
  }

  zoomToMarker(coordinates: [number, number]): void {
    this.map?.jumpTo({center: coordinates, zoom: Math.min(16, this.map.getMaxZoom())});
  }
  invalidateSize(): void { this.map?.resize(); }

  destroyMap(): void {
    this.closePopup();
    this.resolveMapReady?.();
    this.resolveMapReady = undefined;
    this.languageSubscription.unsubscribe();
    this.map?.remove();
    this.map = null;
    this.mapReady = undefined;
    this.defaultTextFields.clear();
  }

  private closePopup(): void {
    this.popup?.remove();
    this.popup = undefined;
  }

  private openFeaturePopup(event: MapLayerMouseEvent): void {
    const feature = event.features?.[0];
    if (!feature || !event.lngLat) return;
    this.openPopup({nombre: feature.properties?.['nombre'], actions: JSON.parse(feature.properties?.['actions'] || '[]')} as DatosMapaModel, event.lngLat);
  }

  private openPopup(data: DatosMapaModel, coordinates: {lng: number; lat: number}): void {
    const content = document.createElement('div');
    content.className = 'pad';
    const header = document.createElement('div');
    header.className = 'flex-inline align-center justify-space-between full-width';

    const title = document.createElement('b');
    title.className = 'flex-1';
    title.textContent = data.nombre || '';

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'btn btn--circle btn--s map-popup__close';
    closeButton.setAttribute('aria-label', 'Cerrar');
    closeButton.addEventListener('click', () => this.closePopup());

    const closeIcon = document.createElement('span');
    closeIcon.className = 'icon icon--close txt-small';
    closeIcon.setAttribute('aria-hidden', 'true');
    closeButton.appendChild(closeIcon);
    header.append(title, closeButton);

    const actions = document.createElement('div');
    actions.className = 'flex flex-column gap-half margin-block-start';
    data.actions?.forEach((action) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn btn--primary btn--s';
      button.textContent = action.label || '';
      button.addEventListener('click', () => this.router.navigateByUrl(action.url));
      actions.appendChild(button);
    });
    content.append(header, actions);
    this.closePopup();
    this.popup = new Popup({closeButton: false, offset: 25}).setDOMContent(content).setLngLat(coordinates).addTo(this.map);
  }

  private prepareStyle(): void {
    if (this.map.getTerrain()) this.map.setTerrain(null);
    this.map.getStyle().layers.forEach((layer) => {
      const sourceLayer = `${'source-layer' in layer ? layer['source-layer'] : ''}`.toLowerCase();
      if (/poi|transit|ferry|bus|shop|shield/i.test(`${layer.id} ${sourceLayer}`)) this.map.setLayoutProperty(layer.id, 'visibility', 'none');
      if (layer.type === 'fill-extrusion') this.map.setLayoutProperty(layer.id, 'visibility', 'none');
      if (layer.id === 'park') this.map.setPaintProperty(layer.id, 'fill-color', '#f1f3eb');
      if (layer.id === 'landcover-grass') this.map.setPaintProperty(layer.id, 'fill-color', '#f3f3ed');
      if (layer.id === 'landcover-wood') this.map.setPaintProperty(layer.id, 'fill-color', '#f1f1eb');
      if (layer.id === 'water') this.map.setPaintProperty(layer.id, 'fill-color', '#a9d8f0');
      const textField = layer.layout?.['text-field'];
      if (!textField) return;
      this.defaultTextFields.set(layer.id, textField);
      if (layer.type !== 'symbol') return;

      const layerId = layer.id.toLowerCase();
      const isBoldLabel = sourceLayer === 'place' && (layerId.includes('country') || layerId.includes('capital'));
      this.map.setLayoutProperty(layer.id, 'text-font', [isBoldLabel ? 'Noto Sans Bold' : 'Noto Sans Regular']);
      this.map.setPaintProperty(layer.id, 'text-color', isBoldLabel ? '#8a8a8a' : '#737373');
    });
  }

  private applyLanguage(language: string): void {
    if (!this.map || !this.defaultTextFields.size) return;
    // Conservamos el texto original porque no todos los nombres tienen traducción.
    const normalizedLanguage = (language || defaultAccountSettings.locale).split('-')[0].toLowerCase();
    this.defaultTextFields.forEach((fallback, layerId) => this.map.setLayoutProperty(layerId, 'text-field', ['coalesce', ['get', `name:${normalizedLanguage}`], fallback as any]));
  }

}
